import React, { Component } from 'react';
import graphql from 'babel-plugin-relay/macro';
import { QueryRenderer, commitLocalUpdate } from 'react-relay';
import { Redirect } from 'react-router-dom';
import AbEnvironment from '../auth/AbEnvironment';
import PropTypes from 'prop-types';
import withStyles from '@mui/styles/withStyles';
import { Button, Checkbox, FormControlLabel, Grid, Paper, Snackbar, SnackbarContent, TextField, Tooltip, Typography } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Delete from '@mui/icons-material/Delete';
import PauseCircleFilled from '@mui/icons-material/PauseCircleFilled';
import PlayCircleFilled from '@mui/icons-material/PlayCircleFilled';
import CheckCircle from '@mui/icons-material/CheckCircle';
import HourglassEmpty from '@mui/icons-material/HourglassEmpty';

import PageTitle from '../ui/PageTitle';
import LoadingSpinner from '../ui/LoadingSpinner';
import SavingSpinner from '../ui/SavingSpinner';
//import PhoneNumberFormat from '../ui/PhoneNumberFormat';
//import Address from '../ui/Address'
import ServerError from '../ui/ServerError';
import EmailDialog from '../ui/EmailDialog';
import UserEmailForm from './UserEmailForm';
import ConfirmationDialog from '../ui/ConfirmationDialog';
import AppBindStyles from '../../AppBindStyles';
import classNames from 'classnames';

import BindCardUnattachedFetch from '../customer/BindCardUnattachedFetch';

import { green, red, amber } from '@mui/material/colors';

const abEnvironment = new AbEnvironment();
const UserEditQuery = graphql`
query UserEditQuery($userId: UUID!) {
  userTByUserId(userId: $userId) {
    userId
    accountId
    accountTByAccountId {
      accountType
      accountName
      useBudgets
      accountTByParentId {
        communicationTemplateTsByAccountId(condition: {templateName: "CustInvite"}) {
          nodes {
            greeting
            body
          }
        }
      }
    }
    userName
    userRole
    userArole
    lastName
    firstName
    email
    phone
    address1
    address2
    city
    state
    country
    postalCode
    marqetaUid
    auth0Uid
    userConfirmDate
    accountLinkTsByUserId {
      totalCount
    }
  }
}
`;

const styleSheet = theme => ({
    root: {
      flexGrow: 1,
      marginTop: 10,
    },
    flex: {
      flex: 1,
    },
    
    paper: {
      padding: theme.spacing(3),
      textAlign: 'left',
      color: theme.palette.text.secondary,
      maxWidth: 980,
      marginTop: 16
    },
    paper2: {
      padding: 12,
      textAlign: 'left',
      color: theme.palette.text.secondary,
    },
    container: {
      display: 'flex',
      flexWrap: 'wrap',
    },
    error: {
      backgroundColor: theme.palette.error.light,
    },
    message: {
      display: 'flex',
      alignItems: 'center',
    },
    close: {
      padding: theme.spacing(0.5),
    },
});

class UserEdit extends Component {

    state = {
        showSpinner: false,
        showSecondarySpinner: false,
        accountId: null,
        userName: null,
        userRole: null,
        lastName: null,
        firstName: null,
        email: null,
        phone: null,
        address1: null,
        address2: null,
        city: null,
        state: null,
        country: null,
        postalCode: null,
        emailDialogOpen: false,
        unattachedAppString: null,
        customerAppId: null,
        blocked: null,
        loginCount: null,
        confirmDelete: false,
        confirmReactivate: false,
        confirmDeactivate: false,
        saveStatus: null,
        saveMessage: null,  
        snackbarOpen: false,
        snackbarDuration: null            
    }

    componentDidMount() {
      this.fetchAuth0User();
      this.unattachedBindCardFetch();

      //see if customerAppID was passed in, if so stuff it in state (used after saving)
      const params = new URLSearchParams(this.props.location.search);
      let theCustomerAppId = params.get('customerAppId');
      //console.log('the passed customer app id is ' + theCustomerAppId);
      if (theCustomerAppId) {
        this.setState({ customerAppId: theCustomerAppId });
      }

      let theAccountId = params.get('accountId');
      if (theAccountId) {
        this.setState({ accountId: theAccountId });
      }
    }

    /*
    * See if the account has any unattached bind cards.  This is used to put together an app list for invite email.
    * This knowledge might be used to invite a customer as last part of subscription flow.
    */
    unattachedBindCardFetch = () => {

      const { logError } = this.props.auth;

      const params = new URLSearchParams(this.props.location.search);
      let acctId = null;
      let acctIdFromParms = params.get('accountId');
      if (acctIdFromParms !== null) {
        acctId = acctIdFromParms;
      } 

      //console.log(acctId);
      if (acctId) {
        BindCardUnattachedFetch.fetch(acctId).toPromise()
        .then(bcuf => {
          //if there are any nodes, grab the app name and stuff into a grammatically correct string
           if (bcuf.getbindcardsunattached && bcuf.getbindcardsunattached.nodes) {
            let appNames = [];
            var i = 0;
            for (i = 0; i < bcuf.getbindcardsunattached.nodes.length; i++) {
  
              if (appNames.indexOf(bcuf.getbindcardsunattached.nodes[i].appName) === -1) {
                appNames.push(bcuf.getbindcardsunattached.nodes[i].appName);
              }
  
            }
            let appNameString = '';
            for (i = 0; i < appNames.length; i++) {
  
              if (i === 0) {
                appNameString = appNames[i];
              } else {
                if (i === (appNames.length - 1)) {
                  appNameString = appNameString + ' and ' + appNames[i];
                } else {
                  appNameString = appNameString + ', ' + appNames[i];
                }
              }
  
            }
            this.setState({unattachedAppString:  appNameString});
           }
  
        })
        .catch(err => {
          //only thing to cause this is perhaps outdated DB structure.
          logError('Exception in UserEdit.js: ' + err);
        })
   
      }
   }

   fetchAuth0User = () => {

    const params = new URLSearchParams(this.props.location.search);
    let userId = params.get('userId');


    const { callPartnerApi } = this.props.auth;

    let aPjwt = abEnvironment.getPjwt();
    

    let payload = {
      operation: "getAuth0User",
      params: {
        pJwt: aPjwt,
        userId: userId
      }
    }
      
    callPartnerApi('appbind/generic',
                   'POST',
                   payload
    )
    .then((response) => {
      return response.json();
    })
    .then(response => {
        //console.log(response);
        if (response.data.logins_count) {
          this.setState({
            loginCount: response.data.logins_count,
            blocked: response.data.blocked        
          })
  
        } else {
          this.setState({
            loginCount: 0,
            blocked: false         
          })
        }
     })
     .catch(error => {
        //console.log('Got error: ' + error);
        //see if it can be fixed
        this.fixAuth0User(userId);

     });
 
  }

  fixAuth0User = () => {

    const params = new URLSearchParams(this.props.location.search);
    let userId = params.get('userId');


    const { callPartnerApi } = this.props.auth;

    let aPjwt = abEnvironment.getPjwt();


    let payload = {
      operation: "fixAuth0User",
      params: {
        pJwt: aPjwt,
        userId: userId
      }
    }
      
    callPartnerApi('appbind/generic',
                   'POST',
                   payload
    )
    .then((response) => {
      return response.json();
    })
    .then(response => {
        //console.log(response);
        if (response.data.logins_count) {
          this.setState({
            loginCount: response.data.logins_count,
            blocked: response.data.blocked        
          })
  
        } else {
          this.setState({
            loginCount: 0,
            blocked: false         
          })
        }
     })
     .catch(error => {
        //console.log('Got error: ' + error);

        
     });
 
  }

  
  handleSnackbarClose = (event, reason) => { 
    this.setState({ snackbarOpen: false, saveStatus: '', saveMessage: '' });
  };

   disableSave(renderProps) {

      const { validateEmail } = this.props.auth;

      if (renderProps === null || renderProps.userTByUserId === null)
        return true;

      let retval = this.disableAttributeCheck(this.state.firstName, renderProps.userTByUserId.firstName) &&
                   this.disableAttributeCheck(this.state.lastName, renderProps.userTByUserId.lastName) &&
                   this.disableAttributeCheck(this.state.email, renderProps.userTByUserId.email) &&
                   this.disableAttributeCheck(this.state.userRole, renderProps.userTByUserId.userRole); // &&
                   //this.disableAttributeCheck(this.state.phone, renderProps.userTByUserId.phone) &&
                   //this.disableAttributeCheck(this.state.address1, renderProps.userTByUserId.address1) &&
                   //this.disableAttributeCheck(this.state.address2, renderProps.userTByUserId.address2) &&
                   //this.disableAttributeCheck(this.state.city, renderProps.userTByUserId.city) &&
                   //this.disableAttributeCheck(this.state.state, renderProps.userTByUserId.state) &&
                   //this.disableAttributeCheck(this.state.country, renderProps.userTByUserId.country) &&
                   //this.disableAttributeCheck(this.state.postalCode, renderProps.userTByUserId.postalCode);

      if (!retval) {
        if (this.state.email) {
          if (!validateEmail(this.state.email)) {
            retval = true;
          }
        }
      }
      return retval;
    }

    disableAttributeCheck(stateVar, propsVar)
    {
        if (stateVar !== null)
        {
            if (propsVar === null && stateVar === '')
                return true;
            
            if (propsVar !== stateVar)
                return false;
        }
        return true;
    }

    goTo(route) {
        this.props.history.replace(`/${route}`);
    }
    
    getValueForRender(state_member, prop_member, theDefault)
    {
      if (state_member != null)
        return state_member;
      
      if (prop_member != null)
        return prop_member;
  
      return theDefault;
    }

    handleChange = name => event => {
      this.setState({
        [name]: event.target.value,
      });
    };

    handleAdminCheckboxChange = (propsUserRole) => {
      let previousValue = this.getValueForRender(this.state.userRole, propsUserRole);
      if (previousValue === 0) {
        this.setState({userRole: 1});
      } else {
        this.setState({userRole: 0});
      }
       
    };
            
    handleEmailClose = () => {
      //console.log(this.state.customerAppId);
      this.setState({emailDialogOpen: false});

      var subscriptionReturn = localStorage.getItem('subscriptionReturn');
      if (subscriptionReturn) {
        var subscriptionLink = '/subscription?customerAppId=' + subscriptionReturn + '&hideback=true';
        localStorage.removeItem('subscriptionReturn');
        this.props.history.replace(subscriptionLink);
      } else if (this.state.customerAppId) {
        if (this.state.accountId) {
          localStorage.setItem('customerTabVal', 3);  //positions user on subscriptions tab
          this.props.history.replace('/customer?accountId=' + this.state.accountId);
        } else {
          this.props.history.replace('/subscription?customerAppId=' + this.state.customerAppId);
        }
      }

    };

    goBack() {
      this.props.history.goBack();
    }
  
    showSpinner() {
      return this.state.showSpinner;
    }

    showSecondarySpinner() {
      return this.state.showSecondarySpinner;
    }

    setCountry    = (country)    => { this.setState({country:    country}); }  
    setRegion     = (region)     => { this.setState({state:      region}); }  
    setCity       = (city)       => { this.setState({city:       city}); }  
    setPostalCode = (postalCode) => { this.setState({postalCode: postalCode}); }  
    setAddress1   = (address1)   => { this.setState({address1:   address1}); }  
    setAddress2   = (address2)   => { this.setState({address2:   address2}); }  

    render() {
        const { isAuthenticated, isAuthenticatedCustomer, isAuthenticatedReseller, isAuthenticatedVendor, isAuthenticatedAppBind, isAccountSuspended, needAssent, logError } = this.props.auth;
        const classes = this.props.classes;

        const statusOk = green[300];
        const statusPause = red[300];
        const statusWaiting = amber[400];

        if (!isAuthenticated() || needAssent()) {
          return (
            <Redirect to='/home' />
          );
        } else if (isAuthenticatedAppBind() || isAuthenticatedReseller() || isAuthenticatedVendor() || isAuthenticatedCustomer()) {

          let isAdmin = false;
          if (abEnvironment.getUserAttribute('u_userRole') === '0') {
            isAdmin = true;
          }

          const params = new URLSearchParams(this.props.location.search);
          let uId = params.get('userId');

          let hideBackParm = params.get('hb'); 


          return (
            <QueryRenderer
              environment={abEnvironment.getEnvironment()}
              query={UserEditQuery}
              variables={{userId: uId}}
              render={({error, props})=> {
                if (error) {
                  logError(error.message);
                  return (
                    <ServerError/>
                  );
                } else if (props) {
                  let accountId   = props.userTByUserId.accountId;
                  let accountType = props.userTByUserId.accountTByAccountId.accountType;
                  let accountName = props.userTByUserId.accountTByAccountId.accountName;
                  let userConfirmDate = props.userTByUserId.userConfirmDate;
                  let userArole = props.userTByUserId.userArole;
                  let magicCount = props.userTByUserId.accountLinkTsByUserId.totalCount;

                  let inviteTemplate = null;
                  if (props.userTByUserId.accountTByAccountId.accountTByParentId && 
                      props.userTByUserId.accountTByAccountId.accountTByParentId.communicationTemplateTsByAccountId &&
                      props.userTByUserId.accountTByAccountId.accountTByParentId.communicationTemplateTsByAccountId.nodes.length === 1) {
                        inviteTemplate = props.userTByUserId.accountTByAccountId.accountTByParentId.communicationTemplateTsByAccountId.nodes[0];
                  }

                  // Invitation send states: 0 - not sent, 1 - sent, 2 - logged in
                  // - for authenticated users, we'll only show 1 or 2, as we currently don't
                  //   have a way to get an invitation sent count.
                  // - for non-authenticated users, we'll look at the account link count (magic
                  //   links) to determine the not sent state.
                  let sendState = 1;  // Default to sent
                  if (userArole === 'appbind_customer_unauth' && magicCount === 0) {
                    sendState = 0;
                  } else if (userArole === 'appbind_customer_unauth' && magicCount > 0) {
                    sendState = 1;
                  } else if (userConfirmDate != null) {
                    sendState = 2;
                  }

                  let usingAccountId =  abEnvironment.getUserAttribute('a_id');

                  let theLinkText = "Settings";

                  if (usingAccountId !== accountId)
                  {
                    //override the admin flag since a non-admin is restricted from editing a user in same account only
                    isAdmin = true;

                    if (accountType === 3)
                    {
                      theLinkText = accountName;
                    }
                    else if (accountType === 4)
                    {
                      theLinkText = accountName;
                    }
                  }

                  var subscriptionReturn = localStorage.getItem('subscriptionReturn');
                  if (subscriptionReturn) {
                    theLinkText = "Subscription";
                  }

                  let thePageTitle = "Edit or invite user for " + accountName;

                  /*let address = {
                    country:    this.getValueForRender(this.state.country,    props.userTByUserId.country,    ''),
                    state:      this.getValueForRender(this.state.state,      props.userTByUserId.state,      ''),
                    city:       this.getValueForRender(this.state.city,       props.userTByUserId.city,       ''),
                    postalCode: this.getValueForRender(this.state.postalCode, props.userTByUserId.postalCode, ''),
                    address1:   this.getValueForRender(this.state.address1,   props.userTByUserId.address1,   ''),
                    address2:   this.getValueForRender(this.state.address2,   props.userTByUserId.address2,   '')
                  };
                  let regionLabel = "Region";*/

                  const emailSubject = 'Invitation from ' + abEnvironment.getUserAttribute('a_accountName');
                  let emailBody = '';
                  if (this.state.unattachedAppString === null || this.state.unattachedAppString === '') {
                    if (inviteTemplate === null) {
                      emailBody = this.getValueForRender(this.state.firstName, props.userTByUserId.firstName, '') + `,\n\nWe'd like to purchase and manage subscriptions for your project.\n\nPlease use our client portal to securely link your credit card, debit card, or bank account. Then we can automatically and transparently expense subscriptions to you.\n\n- ` + abEnvironment.getUserAttribute('a_accountName');
                    } else {
                      if (inviteTemplate.greeting && inviteTemplate.greeting !== '') {
                        emailBody = emailBody + inviteTemplate.greeting + ' ' + this.getValueForRender(this.state.firstName, props.userTByUserId.firstName, '') + ',\n\n';
                      } else {
                        emailBody = emailBody + this.getValueForRender(this.state.firstName, props.userTByUserId.firstName, '') + ',\n\n';
                      }
                      emailBody = emailBody + inviteTemplate.body;
 
                    }
                  } else {
                    emailBody = this.getValueForRender(this.state.firstName, props.userTByUserId.firstName, '') + `,\n\nWe'd like to purchase and manage subscriptions to ` + this.state.unattachedAppString + ` for your project.\n\nPlease use our client portal to securely link your credit card, debit card, or bank account. Then we can automatically and transparently expense subscriptions to you.\n\n- ` + abEnvironment.getUserAttribute('a_accountName');

                  }
        
                  return (
                    <Grid container spacing={3} direction="row" justifyContent="flex-start">
                      <Grid item xs={12}>
                        {!hideBackParm &&
                        <PageTitle title={thePageTitle} showBackButton={true} name={theLinkText} noMargin={true} history={this.props.history}/>}
                        {hideBackParm &&
                        <PageTitle title={thePageTitle} noMargin={true} history={this.props.history}/>}
                      </Grid>

                      <Grid item xs={12}>
                        <Paper className={classes.paper}>
                          <Grid container spacing={2} direction="row" justifyContent="space-around">
                            <Grid item xs={6}>
                              <Typography variant="h6" style={AppBindStyles.sectionTitleFirst}>
                                User Information
                              </Typography>
                            </Grid>
                            <Grid item xs={6} align="right">
                              {this.state.loginCount > 0 && !this.state.blocked &&
                                <CheckCircle htmlColor={statusOk}/>
                              }
                              {this.state.loginCount > 0 && this.state.blocked &&
                                <Tooltip title="User is deactivated.">
                                  <PauseCircleFilled htmlColor={statusPause}/>
                                </Tooltip>
                              }
                              {this.state.loginCount === 0 && 
                                <Tooltip title="Waiting for user to accept invitation.">
                                  <HourglassEmpty htmlColor={statusWaiting}/>
                                </Tooltip>
                              }
  

                          
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <TextField data-recording-ignore="mask"
                                id="first-name" label="First Name" 
                                value={this.getValueForRender(this.state.firstName, 
                                  props.userTByUserId.firstName,
                                  '')}
                                onChange={this.handleChange('firstName')} />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <TextField data-recording-ignore="mask"
                                id="last-name" label="Last Name" 
                                value={this.getValueForRender(this.state.lastName, 
                                  props.userTByUserId.lastName,
                                  '')}
                                onChange={this.handleChange('lastName')} />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <TextField data-recording-ignore="mask"
                                id="email" label="Email" 
                                value={this.getValueForRender(this.state.email, 
                                  props.userTByUserId.email,
                                  '')}
                                onChange={this.handleChange('email')} />
                            </Grid>
                            {isAdmin && userArole !== 'appbind_customer_unauth' &&
                            <Grid item xs={12} sm={6}>
                              <FormControlLabel
                                  control={
                                    <Checkbox
                                      checked={(this.getValueForRender(this.state.userRole, props.userTByUserId.userRole, '1') === 0)}
                                      onChange={() => {this.handleAdminCheckboxChange(props.userTByUserId.userRole)} }
                                      color="primary"
                                    />
                                  }
                                  label="Can administer users"
                                />
                            </Grid>
                            }
                            <Grid item xs={12} sm={6}>
                              {/* remove this grid item when we bring phone back */}
                            </Grid>
                            {/*<Grid item xs={12} sm={6}>
                              <TextField data-recording-ignore="mask"
                                id="phone" label="Phone" 
                                value={this.getValueForRender(this.state.phone, 
                                      props.userTByUserId.phone,
                                      '')}
                                onChange={this.handleChange('phone')}
                                InputProps={{
                                  inputComponent: PhoneNumberFormat,
                                }}
                              />
                              </Grid>*/}
                            {/*<Grid item xs={12}>
                              <Typography variant="h6" style={AppBindStyles.sectionTitle}>
                                Mailing Address
                              </Typography>
                            </Grid>
                            <Grid item xs={12}>
                              <Address
                                address={address}
                                regionLabel={regionLabel}
                                setCountryCodeState={this.setCountry}
                                setRegionCodeState={this.setRegion}
                                setCityState={this.setCity}
                                setPostalCodeState={this.setPostalCode}
                                setAddress1State={this.setAddress1}
                                setAddress2State={this.setAddress2}
                              />
                            </Grid>*/}
                            <Grid container direction="row" justifyContent="flex-start" style={{paddingTop: '16px'}}>
                              <Grid item>
                                {!this.showSpinner() &&
                                <Button disabled={this.disableSave(props)} color="primary" style={AppBindStyles.bottomButton}
                                        variant="contained" onClick={() => {this._updateUser(uId, accountId)}}>
                                  Save
                                </Button> }
                                {this.showSpinner() &&
                                <Button color="primary" variant="contained" style={AppBindStyles.bottomButton}>
                                  <SavingSpinner/></Button>
                                }
                              </Grid>
                              
                                {sendState === 0 && !this.showSpinner() &&
                                <Grid item >
                                  <Button color="primary" variant="outlined" style={AppBindStyles.bottomButton}
                                        disabled={isAccountSuspended()}
                                        onClick={() => {this._resendVerification(usingAccountId, props.userTByUserId)}}>
                                    Send Invitation Email
                                  </Button> 
                                </Grid>
                                }
                                {sendState === 1 && !this.showSpinner() &&
                                <Grid item >
                                  <Button color="primary" variant="outlined" style={AppBindStyles.bottomButton}
                                          disabled={isAccountSuspended()}
                                          onClick={() => {this._resendVerification(usingAccountId, props.userTByUserId)}}>
                                    Resend Invitation Email
                                  </Button> 
                                </Grid>
                                }
                              

                              <Grid item>
                                
                                {isAdmin && this.state.loginCount > 0 && !this.state.blocked && this.showSecondarySpinner() &&
                                <Button color="primary" variant="outlined" style={AppBindStyles.bottomButton}>
                                  <SavingSpinner/>
                                </Button>
                                }

                                {isAdmin && this.state.loginCount > 0 && !this.state.blocked && !this.showSecondarySpinner() &&
                                <Button color="primary" variant="outlined" style={AppBindStyles.bottomButton}
                                        onClick={() => {this.setState({confirmDeactivate:true});}}>
                                  <PauseCircleFilled color="primary"/>&nbsp;&nbsp;Deactivate
                                </Button> }
                              {isAdmin && this.state.loginCount > 0 && this.state.blocked && this.showSecondarySpinner() &&
                                <Button color="primary" variant="outlined" style={AppBindStyles.bottomButton}>
                                  <SavingSpinner/>
                                </Button>
                                }

                                {isAdmin && this.state.loginCount > 0 && this.state.blocked && !this.showSecondarySpinner() &&
                                <Button color="primary" variant="outlined" style={AppBindStyles.bottomButton}
                                        onClick={() => {this.setState({confirmReactivate:true});}}>
                                  <PlayCircleFilled color="primary"/>&nbsp;&nbsp;Reactivate
                                </Button> }

                                {isAdmin && this.state.loginCount === 0 && this.showSecondarySpinner() &&
                                <Button color="primary" variant="outlined" style={AppBindStyles.bottomButton}>
                                  <SavingSpinner/>
                                </Button>
                                }

                                {isAdmin && this.state.loginCount === 0 && !this.showSecondarySpinner() &&
                                <Button color="primary" variant="outlined" style={AppBindStyles.bottomButton}
                                        onClick={() => {this.setState({confirmDelete:true})}}>
                                  <Delete color="primary"/>Delete
                                </Button> 
                                }
                              </Grid>
                            </Grid>
                          </Grid>
                          <ConfirmationDialog
                                  open={this.state.confirmDelete}
                                  onClose={() => { this.setState({confirmDelete:false});}}
                                  onConfirm={() => {this._delete(props.userTByUserId.userId, props.userTByUserId.auth0Uid)}}
                                  onCancel={() => { }}
                                  title="Delete user?"
                                  cancelBtn="Cancel"
                                  okBtn="Ok"
                                >
                                  If you proceed, the user will be deleted.
                            </ConfirmationDialog>  
                            <ConfirmationDialog
                                  open={this.state.confirmReactivate}
                                  onClose={() => { this.setState({confirmReactivate:false});}}
                                  onConfirm={() => { this._reactivate(uId, props.userTByUserId.auth0Uid) }}
                                  onCancel={() => { }}
                                  title="Reactivate user?"
                                  cancelBtn="Cancel"
                                  okBtn="Ok"
                                >
                                  If you proceed, the user will now be able to use AppBind.
                            </ConfirmationDialog>  
                            <ConfirmationDialog
                                  open={this.state.confirmDeactivate}
                                  onClose={() => { this.setState({confirmDeactivate:false});}}
                                  onConfirm={() => {this._deactivate(uId, props.userTByUserId.auth0Uid) }}
                                  onCancel={() => { }}
                                  title="Deactivate user?"
                                  cancelBtn="Cancel"
                                  okBtn="Ok"
                                >
                                  If you proceed, the user will not be able to log in to AppBind.
                            </ConfirmationDialog>  
                            <Snackbar
                              anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'center',
                              }}
                              open={this.state.snackbarOpen}
                              autoHideDuration={this.state.snackbarDuration}
                              onClose={this.handleSnackbarClose}
                            >
                            <SnackbarContent
                                  className={classNames(classes.message, classes[this.state.saveStatus])}
                                  onClose={this.handleSnackbarClose}
                                  message={
                                    <Typography variant="body1">
                                        {this.state.saveMessage}
                                    </Typography>
                                  }
                                  action={[
                                    <IconButton
                                      key="close"
                                      aria-label="Close"
                                      color="inherit"
                                      className={classes.close}
                                      onClick={this.handleSnackbarClose}
                                      size="large">
                                    <CloseIcon />
                                  </IconButton>
                                ]}
                              />
                            </Snackbar>
                        </Paper>
                      
                        <EmailDialog
                          title="Customize your email"
                          open={this.state.emailDialogOpen}
                          onClose={this.handleEmailClose} >
 
                          <UserEmailForm
                            auth={this.props.auth}
                            emailTo={this.getValueForRender(this.state.email, props.userTByUserId.email, '')}
                            testEmailTo={abEnvironment.getUserAttribute('a_email')}
                            emailSubject={emailSubject}
                            emailTextBody={emailBody}
                            userId={props.userTByUserId.userId}
                            auth0Uid={props.userTByUserId.auth0Uid}
                            pAccountId={usingAccountId}
                            accountId={props.userTByUserId.accountId}
                            userArole={props.userTByUserId.userArole} 
                            onClose={this.handleEmailClose}>
                          </UserEmailForm>
                        </EmailDialog>
                      </Grid>
                    </Grid>
                  );
                }
                return <div><LoadingSpinner/></div>
              }}
            />
          )
        } else {
          return (
            <Redirect to='/home' />
          );
        }
    }

    /*
     * Call the api server to update the user, and update the local relay store
     * with the results.
     */
    _updateUser = (userId, accountId) => {
      const { callPartnerApi } = this.props.auth;

      this.setState({ showSpinner: true });
      const { lastName, firstName, email, phone,
              address1, address2, city, state, country, postalCode, userRole } = this.state;

      let payload = {
        operation: "updateUser",
        params: {
          pJwt: abEnvironment.getPjwt(),
          user: {
            userId: userId,
            accountId: accountId,
            userRole: userRole,
            lastName: lastName,
            firstName: firstName,
            email: email,
            phone: phone,
            address1: address1,
            address2: address2,
            city: city,
            state: state,
            country: country,
            postalCode: postalCode
          }
        }
      }
              
      // Call API
      callPartnerApi('appbind/generic',
                     'POST',
                     payload
      )
      .then(resp => {
        return resp.json();
      })
      .then(response => {
        if (response != null && response.status === 0 && response.userT != null) {
          // If redirecting somewhere else, no need to set the local store
          if (this.state.customerAppId) {
            this.setState({ showSpinner: false });
            var subscriptionReturn = localStorage.getItem('subscriptionReturn');
            if (subscriptionReturn) {
              var subscriptionLink = '/subscription?customerAppId=' + subscriptionReturn + '&hideback=true';
              localStorage.removeItem('subscriptionReturn');
              this.props.history.replace(subscriptionLink);
            } else if (this.state.accountId) {
              localStorage.setItem('customerTabVal', 2);  //positions user on subscriptions tab
              this.props.history.replace('/customer?accountId=' + this.state.accountId);
            } else {
              this.props.history.replace('/subscription?customerAppId=' + this.state.customerAppId);
            }
          } else {
            // Set local store with result
            let rUserT = response.userT;

            commitLocalUpdate(abEnvironment.getEnvironment(), store => {
              let theIdString = 'userTByUserId(userId:"' + userId + '")';
              let suser = store.getRoot().getLinkedRecord(theIdString);
              if (suser != null) {
                if (rUserT.userRole != null)   { suser.setValue(rUserT.userRole,   'userRole'); }
                if (rUserT.lastName != null)   { suser.setValue(rUserT.lastName,   'lastName'); }
                if (rUserT.firstName != null)  { suser.setValue(rUserT.firstName,  'firstName'); }
                if (rUserT.email != null)      { suser.setValue(rUserT.email,      'email'); }
                if (rUserT.phone != null)      { suser.setValue(rUserT.phone,      'phone'); }
                if (rUserT.address1 != null)   { suser.setValue(rUserT.address1,   'address1'); }
                if (rUserT.address2 != null)   { suser.setValue(rUserT.address2,   'address2'); }
                if (rUserT.city != null)       { suser.setValue(rUserT.city,       'city'); }
                if (rUserT.state != null)      { suser.setValue(rUserT.state,      'state'); }
                if (rUserT.country != null)    { suser.setValue(rUserT.country,    'country'); }
                if (rUserT.postalCode != null) { suser.setValue(rUserT.postalCode, 'postalCode'); }
              }
            });
          }

          this.setState({ showSpinner: false });
        } else {
          //console.log(response);
          if (response.err && response.err.abMessage) {
            this.setState({ showSpinner: false, snackbarOpen: true, saveStatus: 'error', saveMessage: response.err.abMessage, snackbarDuration: 3000 });
          } else {
            this.setState({ showSpinner: false, snackbarOpen: true, saveStatus: 'error', saveMessage: 'Problem updating user', snackbarDuration: 3000 });
            
          }

        }
      })
      .catch(error => {
        // Reload?
        this.setState({ showSpinner: false });
      });

    }

    _resendVerification = (pAccountId, theprops) => {

      if (theprops.userArole === 'appbind_customer_unauth') {

        this._showEmailDialog(theprops.accountTByAccountId.accountType, theprops.accountTByAccountId.useBudgets);
        
      } else {

        this.setState({ showSpinner: true });
        const { callPartnerApi } = this.props.auth;
  
        let aPjwt = abEnvironment.getPjwt();
        
        let tUserId  = theprops.userId;
        let tAccountId = theprops.accountId;
  
  
        let payload = {
          operation: "resendUserInviteEmail",
          params: {
            pJwt: aPjwt,
            pAccountId: pAccountId,
            accountId: tAccountId,
            abUserId: tUserId
          }
        }
        
        callPartnerApi('appbind/generic',
                       'POST',
                       payload
        )
        .then(response => {
          this.setState({ showSpinner: false });
          if (this.state.customerAppId) {
            if (this.state.accountId) {
              localStorage.setItem('customerTabVal', 2);  //positions user on subscriptions tab
              this.props.history.replace('/customer?accountId=' + this.state.accountId);
            } else {
              this.props.history.replace('/subscription?customerAppId=' + this.state.customerAppId);
            }
          }
        })
        .catch(error => {
          this.setState({ showSpinner: false });
          // console.log('Got error: ' + error);
        });
  
      }

    }

    _deactivate = (userId, auth0UserId) => {

      this.setState({ showSecondarySpinner: true });
      const { callPartnerApi } = this.props.auth;
      let aPjwt = abEnvironment.getPjwt();

      let payload = {
        operation: "changeAuth0UserBlockStatus",
        params: {
          pJwt: aPjwt,
          userId: userId,
          auth0UserId: auth0UserId,
          status: true
        }
      }
      
      callPartnerApi('appbind/generic',
                      'POST',
                      payload
      )
      .then(response => {
        window.location.reload();
      })
      .catch(error => {
        this.setState({ showSecondarySpinner: false });
      });

    }

    _reactivate = (userId, auth0UserId) => {

      this.setState({ showSecondarySpinner: true });
      const { callPartnerApi } = this.props.auth;
      let aPjwt = abEnvironment.getPjwt();


      let payload = {
        operation: "changeAuth0UserBlockStatus",
        params: {
          pJwt: aPjwt,
          userId: userId,
          auth0UserId: auth0UserId,
          status: false
        }
      }
      
      callPartnerApi('appbind/generic',
                      'POST',
                      payload
      )
      .then(response => {
        window.location.reload();
      })
      .catch(error => {
        this.setState({ showSecondarySpinner: false });
      });

    }

    _delete = (userID, Auth0UserId) => {


      this.setState({ showSecondarySpinner: true });
      const { callPartnerApi } = this.props.auth;
      let aPjwt = abEnvironment.getPjwt();


      let payload = {
        operation: "deleteUser",
        params: {
          pJwt: aPjwt,
          userId: userID,
          auth0UserId: Auth0UserId
        }
      }
      
      callPartnerApi('appbind/generic',
                      'POST',
                      payload
      )
      .then((response) => {
        return response.json();
      })  
      .then(response => {

        if (response.err) {
          this.setState({ showSecondarySpinner: false, snackbarOpen: true, saveStatus: 'error', saveMessage: response.err, snackbarDuration: 3000 });
        } else {
          this.goBack();
        }
      })
      .catch(error => {
        this.setState({ showSecondarySpinner: false });
      });

    }
    
    _showEmailDialog = (accountType, useBudgets) => {

        this.setState({ emailDialogOpen: true });

    }

}

UserEdit.propTypes = {
    classes: PropTypes.object.isRequired,
};

export default withStyles(styleSheet)(UserEdit);